'use client';

import { useEffect, useState, FC, ReactNode, MutableRefObject } from 'react';
import { CatButton } from 'components/blocks-components';
import cx from 'classnames';
import { useExpander } from '../../hooks/useExpander';
import { shouldShowButton } from './utils';
import { HeightLimitedExpanderProps } from './HeightLimitedExpanderProps.types';
import styles from './height-limited-expander.module.scss';

const DEFAULT_MAX_HEIGHT = 400;

const HeightLimitedExpander: FC<HeightLimitedExpanderProps> = ({
  buttonClassName = '',
  className = '',
  buttonSize = 'sm',
  buttonType = 'inline',
  containsHtml = false,
  children = '',
  expanded = false,
  hideGradient = false,
  id,
  lessButtonText,
  maxHeight = DEFAULT_MAX_HEIGHT,
  moreButtonText,
  setExpanded
}) => {
  const [showButton, setShowButton] = useState(false);
  const { isExpanded, ref, toggle } = useExpander({
    disable: !showButton, // if the button isn't shown, then there is no expander functionality needed
    expanded,
    minHeight: maxHeight,
    setExpanded
  });

  useEffect(() => {
    const determineButtonVisibility = (): void => {
      const scrollHeight = ref.current?.scrollHeight ?? maxHeight;
      const buttonVisible = shouldShowButton(scrollHeight, maxHeight);
      setShowButton(buttonVisible);
    };

    determineButtonVisibility();

    window.addEventListener('resize', determineButtonVisibility);
    return (): void =>
      window.removeEventListener('resize', determineButtonVisibility);
  }, [maxHeight, ref, toggle]);

  const getContents = (
    contentsParam: ReactNode,
    containsHtmlParam: boolean
  ): ReactNode =>
    typeof contentsParam === 'string' && containsHtmlParam ? (
      <div
        className="transition-expand"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: contentsParam }}
        id={id}
        ref={ref as MutableRefObject<HTMLDivElement>}
      />
    ) : (
      <div
        className="transition-expand"
        id={id}
        ref={ref as MutableRefObject<HTMLDivElement>}
      >
        {contentsParam}
      </div>
    );

  const btnWrapperClass = cx({
    'd-none': !showButton,
    [styles['view-more__btn']]: !hideGradient && !isExpanded
  });

  return (
    <div className={cx('position-relative', className)}>
      {getContents(children, containsHtml)}
      <div data-testid="button-container" className={btnWrapperClass}>
        <CatButton
          data-testid="view-more-less__btn"
          className={`${buttonClassName}`}
          variant={buttonType}
          size={buttonSize}
          onClick={(): void => toggle()}
        >
          {isExpanded ? lessButtonText : moreButtonText}
        </CatButton>
      </div>
    </div>
  );
};

export default HeightLimitedExpander;
